/**
 * Dialog360 Outbound Tab
 * Monitor outbound WhatsApp messages — the queue plus sent / delivered / read / failed
 * history (worker-queued and console-sent share the same store).
 */

import { useEffect, useState } from 'react';
import { IconRefresh, IconEye, IconSend } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  Badge,
  Card,
  ActionIcon,
  EmptyState,
  Textarea,
  Modal,
  Tooltip,
} from '../../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../../components/advanced';
import { useDialog360 } from '../hooks/useDialog360';
import type { AppWhatsAppOutboundMessage } from '../dialog360.types';
import { outboundStatusColor, formatIsoDateTime, formatStampDecimal } from '../dialog360.types';
import { decodeMessageBody } from '../../_shared/decodeBody';

export function Dialog360OutboundTab() {
  const { loading, messages, fetchMessages } = useDialog360();

  const [selected, setSelected] = useState<AppWhatsAppOutboundMessage | null>(null);
  const [detailOpened, setDetailOpened] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const openDetail = (item: AppWhatsAppOutboundMessage) => {
    setSelected(item);
    setDetailOpened(true);
  };

  const closeDetail = () => {
    setDetailOpened(false);
    setSelected(null);
  };

  const columns: DataTableColumn<AppWhatsAppOutboundMessage & { id: number }>[] = [
    {
      key: 'recipient',
      label: 'Recipient',
      sortable: true,
      render: (value) => <Text ff="monospace" size="sm">{(value as string) || '-'}</Text>,
    },
    {
      key: 'messageType',
      label: 'Type',
      filterable: true,
      width: 130,
      render: (value) => (
        <Badge variant="light" color="grape" size="sm">
          {(value as string) || '-'}
        </Badge>
      ),
    },
    {
      key: 'body',
      label: 'Message',
      render: (value, row) => {
        const text = decodeMessageBody(value as string) || decodeMessageBody(row.caption) || (row.templateName ? `Template: ${row.templateName}` : '-');
        return <Text size="sm" lineClamp={1}>{text}</Text>;
      },
    },
    {
      key: 'status',
      label: 'Status',
      filterable: true,
      sortable: true,
      width: 130,
      render: (value, row) => {
        const badge = (
          <Badge variant="light" color={outboundStatusColor(value as string)} size="sm">
            {(value as string) || 'Unknown'}
          </Badge>
        );
        // Skipped (or failed for good): say why, and whether the SMS went instead, on hover.
        return row.failureReason && ['skipped', 'failed'].includes(String(value || '').toLowerCase()) ? (
          <Tooltip label={row.failureReason} multiline w={320} withArrow>
            <span>{badge}</span>
          </Tooltip>
        ) : (
          badge
        );
      },
    },
    {
      key: 'retryCount',
      label: 'Retries',
      sortable: true,
      render: (value, row) => (
        <Text size="sm">{value ?? 0}{row.maxRetryCount ? ` / ${row.maxRetryCount}` : ''}</Text>
      ),
    },
    {
      key: 'sentDateTime',
      label: 'Date',
      sortable: true,
      render: (value, row) => (
        <Text size="sm">{value ? formatIsoDateTime(value as string) : formatStampDecimal(row.createdDateTime)}</Text>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            onClick={() => openDetail(row)}
            title="View Message"
          >
            <IconEye size={16} />
          </ActionIcon>
        </Group>
      ),
    },
  ];

  const data = messages.map((item) => ({ ...item, id: item.messageID }));

  return (
    <Stack gap="md">
      <Card>
        <Group justify="space-between">
          <div>
            <Text fw={500} size="lg">Outbound Messages</Text>
            <Text size="sm" c="dimmed">
              Queue and sent / delivered / read / failed history for outbound WhatsApp messages
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={() => { setPage(1); fetchMessages(); }}
            loading={loading}
            gradientVariant="primary"
          >
            Refresh
          </Button>
        </Group>
      </Card>

      <Card>
        {messages.length === 0 && !loading ? (
          <EmptyState
            icon={<IconSend size={48} />}
            title="No Outbound Messages"
            description="Messages sent from the Compose tab or queued by the system will appear here."
            actionLabel="Refresh"
            onAction={() => fetchMessages()}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data}
            enableInternalSearch
            searchPlaceholder="Search outbound messages..."
            enableInternalSort
            pagination={{
              page,
              pageSize,
              total: messages.length,
              onPageChange: setPage,
              onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
            }}
          />
        )}
      </Card>

      <Modal opened={detailOpened} onClose={closeDetail} title="Outbound Message Details" size="lg">
        {selected && (
          <Stack gap="sm">
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Recipient</Text>
              <Text size="sm" ff="monospace">{selected.recipient || '-'}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Type</Text>
              <Badge variant="light" color="grape" size="sm">{selected.messageType || '-'}</Badge>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Status</Text>
              <Badge variant="light" color={outboundStatusColor(selected.status)} size="sm">
                {selected.status || 'Unknown'}
              </Badge>
            </Group>
            {selected.templateName && (
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" c="dimmed">Template</Text>
                <Text size="sm">{selected.templateName} ({selected.templateLanguage || 'en'})</Text>
              </Group>
            )}
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">WhatsApp Message ID</Text>
              <Text size="sm" ff="monospace">{selected.whatsAppMessageId || selected.externalMessageId || '-'}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Retries</Text>
              <Text size="sm">{selected.retryCount ?? 0} / {selected.maxRetryCount ?? 0}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Queued</Text>
              <Text size="sm">{formatIsoDateTime(selected.queuedDateTime)}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Sent</Text>
              <Text size="sm">{formatIsoDateTime(selected.sentDateTime)}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Delivered</Text>
              <Text size="sm">{formatIsoDateTime(selected.deliveredDateTime)}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Read</Text>
              <Text size="sm">{formatIsoDateTime(selected.readDateTime)}</Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c="dimmed">Created</Text>
              <Text size="sm">{formatStampDecimal(selected.createdDateTime)}</Text>
            </Group>

            {decodeMessageBody(selected.body) && (
              <Textarea label="Body" value={decodeMessageBody(selected.body)} readOnly autosize minRows={2} maxRows={6} />
            )}
            {selected.failureReason && (
              <Textarea label={String(selected.status || '').toLowerCase() === 'skipped' ? 'Why WhatsApp was skipped' : 'Failure Reason'} value={`${selected.errorCode ? selected.errorCode + ': ' : ''}${selected.failureReason}`} readOnly autosize minRows={1} maxRows={4} />
            )}
            {selected.response && (
              <Textarea label="Provider Response" value={selected.response} readOnly autosize minRows={2} maxRows={8} />
            )}

            <Group justify="flex-end">
              <Button variant="subtle" onClick={closeDetail}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
